import React, { useState, useEffect } from 'react';
import BackButton from '../../../components/common/BackButton';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import {
  getStocks,
  createStock,
  updateStock,
  addStockQuantity,
  deleteStock,
} from '../../../services/stockService';
import { getApiErrorMessage } from '../../../services/http';

const StockManagement = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddStockForm, setShowAddStockForm] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ show: false, stockId: null });
  const [error, setError] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    type: 'roll',
    unit: 'meters',
    currentQuantity: 0,
    alertThreshold: 20,
    costPerUnit: 0,
    description: ''
  });

  const [addStockData, setAddStockData] = useState({
    quantity: 0,
    reason: '',
    notes: ''
  });

  useEffect(() => {
    fetchStocks();
  }, []);

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const data = await getStocks();
      setStocks(data);
      setError('');
    } catch (error) {
      console.error('Error fetching stocks:', error);
      setError(getApiErrorMessage(error, 'Failed to fetch stocks'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddStockInputChange = (e) => {
    const { name, value } = e.target;
    setAddStockData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'roll',
      unit: 'meters',
      currentQuantity: 0,
      alertThreshold: 20,
      costPerUnit: 0,
      description: ''
    });
    setAddStockData({
      quantity: 0,
      reason: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showEditForm) {
        await updateStock(selectedStock._id, formData);
      } else {
        await createStock(formData);
      }

      fetchStocks();
      setShowAddForm(false);
      setShowEditForm(false);
      resetForm();
      setSelectedStock(null);
      setError('');
    } catch (error) {
      console.error('Error saving stock:', error);
      setError(getApiErrorMessage(error, 'Failed to save stock'));
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await addStockQuantity(selectedStock._id, addStockData);
      fetchStocks();
      setShowAddStockForm(false);
      resetForm();
      setSelectedStock(null);
      setError('');
    } catch (error) {
      console.error('Error adding stock:', error);
      setError(getApiErrorMessage(error, 'Failed to add stock'));
    }
  };

  const handleEdit = (stock) => {
    setSelectedStock(stock);
    setFormData({
      name: stock.name,
      type: stock.type,
      unit: stock.unit,
      currentQuantity: stock.currentQuantity,
      alertThreshold: stock.alertThreshold,
      costPerUnit: stock.costPerUnit || 0,
      description: stock.description || ''
    });
    setShowEditForm(true);
  };

  const handleAddStockQuantity = (stock) => {
    setSelectedStock(stock);
    setShowAddStockForm(true);
  };

  const handleDelete = async () => {
    try {
      await deleteStock(deleteDialog.stockId);
      fetchStocks();
      setDeleteDialog({ show: false, stockId: null });
      setError('');
    } catch (error) {
      console.error('Error deleting stock:', error);
      setError(getApiErrorMessage(error, 'Failed to delete stock'));
    }
  };

  const getStatusColor = (stock) => {
    if (stock.currentQuantity === 0) return 'text-red-600 bg-red-100';
    if (stock.isLowStock) return 'text-orange-600 bg-orange-100';
    return 'text-green-600 bg-green-100';
  };

  const getStatusText = (stock) => {
    if (stock.currentQuantity === 0) return 'Out of Stock';
    if (stock.isLowStock) return 'Low Stock';
    return 'In Stock';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Stock Management</h1>
          <p className="text-gray-600 mt-2">Manage your printing materials and track inventory levels</p>
        </div>
        <BackButton to="/dashboard" />
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Stock Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Total Items</h3>
          <p className="text-2xl font-bold text-blue-600">{stocks.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Low Stock</h3>
          <p className="text-2xl font-bold text-orange-600">
            {stocks.filter(s => s.isLowStock).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Out of Stock</h3>
          <p className="text-2xl font-bold text-red-600">
            {stocks.filter(s => s.currentQuantity === 0).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Healthy</h3>
          <p className="text-2xl font-bold text-green-600">
            {stocks.filter(s => !s.isLowStock && s.currentQuantity > 0).length}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => {
            setShowAddForm(true);
            resetForm();
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          Add New Stock Item
        </button>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Material
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert Threshold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stocks.map((stock) => (
                <tr key={stock._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{stock.name}</div>
                      <div className="text-sm text-gray-500">{stock.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {stock.type} ({stock.unit})
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {stock.currentQuantity} {stock.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {stock.alertThreshold} {stock.unit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(stock)}`}>
                      {getStatusText(stock)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(stock)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleAddStockQuantity(stock)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Add Stock
                      </button>
                      <button
                        onClick={() => setDeleteDialog({ show: true, stockId: stock._id })}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Stock Form Modal */}
      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showEditForm ? 'Edit Stock Item' : 'Add New Stock Item'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type</label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="roll">Roll</option>
                        <option value="piece">Piece</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Unit</label>
                      <select
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="meters">Meters</option>
                        <option value="pieces">Pieces</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Quantity</label>
                      <input
                        type="number"
                        name="currentQuantity"
                        value={formData.currentQuantity}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Alert Threshold</label>
                      <input
                        type="number"
                        name="alertThreshold"
                        value={formData.alertThreshold}
                        onChange={handleInputChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cost Per Unit (KSh)</label>
                    <input
                      type="number"
                      name="costPerUnit"
                      value={formData.costPerUnit}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setShowEditForm(false);
                      resetForm();
                      setSelectedStock(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    {showEditForm ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Quantity Modal */}
      {showAddStockForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Stock to {selectedStock?.name}
              </h3>
              <form onSubmit={handleAddStock}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quantity to Add</label>
                    <input
                      type="number"
                      name="quantity"
                      value={addStockData.quantity}
                      onChange={handleAddStockInputChange}
                      min="0.01"
                      step="0.01"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Reason</label>
                    <input
                      type="text"
                      name="reason"
                      value={addStockData.reason}
                      onChange={handleAddStockInputChange}
                      placeholder="e.g., New purchase, Return, etc."
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={addStockData.notes}
                      onChange={handleAddStockInputChange}
                      rows="3"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddStockForm(false);
                      resetForm();
                      setSelectedStock(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-green-700"
                  >
                    Add Stock
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.show}
        onClose={() => setDeleteDialog({ show: false, stockId: null })}
        onConfirm={handleDelete}
        title="Delete Stock Item"
        message="Are you sure you want to delete this stock item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="red"
      />
    </div>
  );
};

export default StockManagement;
